/**
 * Generic Supabase query hook factory.
 *
 * Encapsulates the boilerplate shared by 20+ data-fetching hooks:
 * - Loading / error / data state management
 * - isMounted ref for safe cleanup after unmount
 * - Automatic fetch on mount, with dependency-based re-fetch
 * - Dependent queries (skip when `enabled` is false, e.g. no user)
 * - A stable `refetch` function callers can invoke after mutations
 *
 * Usage (list):
 *   const { data, loading, error, refetch } = useSupabaseQuery<Firearm>({
 *     queryFn: (supabase) =>
 *       supabase.from('firearms').select('*').eq('user_id', user.id),
 *     mapper: (data) => mapRows<Firearm>(data),
 *   })
 *
 * Usage (single):
 *   const { data, loading, error, refetch } = useSupabaseQuerySingle<Profile>({
 *     queryFn: (client) =>
 *       client.from('profiles').select('*').eq('id', user.id).single(),
 *     mapper: (data) => mapRow<Profile>(data as Record<string, unknown>),
 *   })
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'
import type { PostgrestError } from '@supabase/supabase-js'

// ── Types ──────────────────────────────────────────────────────────

type SupabaseClient = typeof supabase

interface SupabaseQueryResult {
  data: unknown
  error: PostgrestError | null
}

export interface UseSupabaseQueryOptions<T> {
  /** Build and return the Supabase query. Receives the client so callers don't need to import it. */
  queryFn: (client: SupabaseClient) => PromiseLike<SupabaseQueryResult>
  /** Map the raw Supabase response data into the desired shape. */
  mapper: (data: unknown) => T
  /** When false the query is skipped (data resets to `initialData`). Defaults to `!!user`. */
  enabled?: boolean
  /** Value used while loading or when the query is disabled. */
  initialData?: T
  /**
   * Additional dependencies that should trigger a re-fetch.
   * The hook always re-fetches when `enabled` or the user changes;
   * this allows adding extra deps (e.g. a competitionId parameter).
   */
  deps?: unknown[]
}

export interface UseSupabaseQueryResult<T> {
  data: T
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// ── Private base hook (shared logic) ──────────────────────────────

function useSupabaseQueryBase<TData>(
  options: UseSupabaseQueryOptions<TData> & { initialData: TData },
): UseSupabaseQueryResult<TData> {
  const { user } = useAuth()
  const { queryFn, mapper, enabled = !!user, initialData, deps = [] } = options

  const [data, setData] = useState<TData>(initialData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setData(initialData)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data: responseData, error: queryError } = await queryFn(supabase)

      if (!isMounted.current) return

      if (queryError) {
        setError(queryError.message)
        setData(initialData)
      } else {
        setData(mapper(responseData))
      }
    } catch (err) {
      if (!isMounted.current) return
      setError(err instanceof Error ? err.message : 'An unexpected error occurred')
      setData(initialData)
    }

    if (isMounted.current) {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryFn/mapper are caller-created inline; deps array provides the stable dependency list
  }, [enabled, ...deps])

  useEffect(() => {
    isMounted.current = true
    fetchData()
    return () => {
      isMounted.current = false
    }
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

// ── Hook for lists (T[]) ───────────────────────────────────────────

export function useSupabaseQuery<T>(
  options: UseSupabaseQueryOptions<T[]>,
): UseSupabaseQueryResult<T[]> {
  return useSupabaseQueryBase<T[]>({
    ...options,
    initialData: options.initialData ?? ([] as T[]),
  })
}

// ── Hook for single items (T | null) ──────────────────────────────

export function useSupabaseQuerySingle<T>(
  options: UseSupabaseQueryOptions<T | null>,
): UseSupabaseQueryResult<T | null> {
  return useSupabaseQueryBase<T | null>({
    ...options,
    initialData: options.initialData ?? null,
  })
}
